import type { CueId, CueParams } from '@sol-pool/engine-physics'
import { ATRIBUTOS, paramsDePontos, type Pontos } from './escala'
import { COLECAO_GDM_TESTE, pontosGdmDoMint } from './gdm-pontos'
import { PONTOS_DO_GRATUITO, TACO_GRATUITO_PADRAO, TACOS_GRATUITOS } from './gratuitos'
import {
  NIVEIS_ZERO,
  niveisGastos,
  orcamentoDeUpgrade,
  podeSubir,
  pontosComNiveis,
  type Atributo,
  type Niveis,
} from './niveis'
import { extraDaLinha, pontosDoModelo, perfilDaLinha, type ModeloDePontos } from './parceira/modelos'
import type { LinhaDaTabela } from './parceira/tipos'
import { pontosDoTaco } from './pontos'
import { CURVA_PADRAO, precoDoNivel, type CurvaDePreco } from './preco'
import { RULESET_V3, type PerfilDeTaco, type Ruleset } from './ruleset'

/** O que o cliente PEDE. O servidor confere; nunca confia. */
export type PedidoDeTaco = { id: CueId } | { mint: string }

export type OrigemDoTaco =
  | { origem: 'gratuito'; id: CueId }
  | { origem: 'nft'; mint: string; colecao: string }
  /** NFT de coleção parceira: o taco é um registro NOSSO ligado ao mint. */
  | { origem: 'parceira'; mint: string; colecao: string; numero: number }

/**
 * A LOJA de upgrade de um taco, pronta para desenhar: onde ele está, quanto
 * ainda pode gastar, e — por atributo — o preço do próximo nível ou o motivo
 * de não haver próximo nível.
 *
 * `preco` e `motivo` são espelhos: um é nulo exatamente quando o outro não é.
 * Vai assim para o cliente não precisar reproduzir `podeSubir` nem a curva de
 * preço — ele desenha o botão com o que chegou, e o servidor confere de novo
 * na hora de cobrar.
 *
 * O preço é STRING porque lamports não cabem com segurança em `number` no
 * JS (acima de 2^53 a conta erra), a mesma razão de `tacos.mint.estado`.
 */
export type UpgradeDoTaco = {
  niveis: Niveis
  gastos: number
  orcamento: number
  /**
   * O teto de níveis de UM atributo — o `nivelMaximo` do ruleset.
   *
   * Vai junto porque o cartão escreve "nv 1/5" ao lado de cada barra, e o 5 é
   * regra de equilíbrio: quem a conhece é o ruleset, que mora no servidor.
   * Uma constante repetida no cliente ficaria mentindo no primeiro
   * rebalanceio — e mentindo justamente sobre quanto ainda dá para comprar.
   *
   * É diferente de `orcamento`: aquele é o total de níveis do TACO (por
   * raridade), este é o pico que UM atributo aguenta.
   */
  nivelMaximo: number
  preco: Record<Atributo, string | null>
  motivo: Record<Atributo, 'orcamento' | 'nivelMaximo' | 'tetoDoAtributo' | null>
  /**
   * Quanto já foi PAGO por este taco, em lamports, somando todos os níveis
   * comprados. String pela mesma razão de `preco`.
   *
   * Vem de fora (do livro `taco_upgrade`) e não é derivado da curva: quem
   * pagou a mais pagou a mais, e a curva de hoje pode não ser a de ontem.
   * Recalcular aqui daria um número plausível e errado — e é dinheiro do
   * jogador que está sendo mostrado.
   */
  investidoLamports: string
}

/**
 * Um taco pronto para jogar: de onde veio, o que é, os pontos (para as
 * barras) e os bps (para a física). Calculado UMA vez, no servidor, e mandado
 * inteiro ao cliente — que desenha e nada mais.
 */
export type TacoEmJogo = OrigemDoTaco & {
  perfil: PerfilDeTaco
  pontos: Pontos
  params: CueParams
  /** Caminho da arte, por arquétipo e raridade. */
  skin: string
  /**
   * A loja de upgrade — nula nos gratuitos.
   *
   * Nulo e não "objeto zerado": o taco gratuito não é de ninguém e não se
   * evolui. Um objeto zerado faria a tela desenhar uma loja que só sabe
   * recusar, e o servidor teria de distinguir "sem orçamento" de "nem se
   * aplica" pelo mesmo campo.
   */
  upgrade: UpgradeDoTaco | null
}

/**
 * O caminho da arte deste taco.
 *
 * A coleção só tem imagem das raridades CUNHÁVEIS — foram elas que foram
 * geradas e normalizadas em `apps/web/public/tacos/nft/`. O `gratuito` cai na
 * arte common do arquétipo em vez de pedir um arquivo que não existe: uma
 * skin ausente deixaria o taco de todo jogador novo desenhado como a linha de
 * reserva, que é justamente o que as artes existem para substituir.
 *
 * A raridade continua legível na tela — pela BORDA do cartão, que tem uma cor
 * própria para `gratuito`. A arte diz o arquétipo; a borda diz a raridade.
 */
export const skinDoTaco = (p: PerfilDeTaco): string =>
  `/tacos/nft/${p.arquetipo}-${p.raridade === 'gratuito' ? 'common' : p.raridade}.png`

/**
 * Os pontos BASE de um taco, sem níveis: pelo ruleset, menos o grátis padrão
 * (`PONTOS_DO_GRATUITO`, um terço do full) e os tacos de teste da Gangue do
 * Macaco, que têm o modelo próprio de `gdm-pontos.ts`.
 *
 * Uma porta só para a mesa, a loja de upgrade e o servidor: se um deles
 * calculasse por fora, o taco da tela e o da física poderiam discordar.
 */
export function pontosBase(origem: OrigemDoTaco, perfil: PerfilDeTaco, ruleset: Ruleset = RULESET_V3): Pontos {
  if (origem.origem === 'gratuito' && origem.id === TACO_GRATUITO_PADRAO) return { ...PONTOS_DO_GRATUITO }
  const gdm = origem.origem === 'nft' && origem.colecao === COLECAO_GDM_TESTE ? pontosGdmDoMint(origem.mint) : null
  return gdm ?? pontosDoTaco(perfil, ruleset)
}

function lojaDeUpgrade(
  perfil: PerfilDeTaco,
  base: Pontos,
  niveis: Niveis,
  curva: CurvaDePreco,
  investidoLamports: bigint,
): UpgradeDoTaco {
  // `base` são os pontos SEM níveis: é isso que `podeSubir` espera — ela soma
  // os níveis por dentro para saber onde o atributo está hoje.
  const preco = {} as UpgradeDoTaco['preco']
  const motivo = {} as UpgradeDoTaco['motivo']
  for (const k of ATRIBUTOS) {
    const r = podeSubir(base, perfil.raridade, niveis, k, RULESET_V3)
    motivo[k] = r === 'ok' ? null : r
    // O preço é o do PRÓXIMO nível deste atributo (a curva é por atributo,
    // não por taco): quem já comprou dois paga o terceiro.
    preco[k] = r === 'ok' ? precoDoNivel(niveis[k] + 1, curva).toString() : null
  }
  return {
    niveis: { ...niveis },
    gastos: niveisGastos(niveis),
    orcamento: orcamentoDeUpgrade(perfil.raridade, RULESET_V3),
    nivelMaximo: RULESET_V3.nivelMaximo,
    preco,
    motivo,
    // Negativo não existe em lamports pagos: uma soma torta vinda do banco
    // viraria "investido -0.5 SOL" na tela. Preso em zero, o pior caso é
    // esconder um número errado.
    investidoLamports: (investidoLamports > 0n ? investidoLamports : 0n).toString(),
  }
}

function montar(
  origem: OrigemDoTaco,
  perfil: PerfilDeTaco,
  niveis: Niveis = NIVEIS_ZERO,
  curva: CurvaDePreco = CURVA_PADRAO,
  investidoLamports = 0n,
): TacoEmJogo {
  const base = pontosBase(origem, perfil)
  const pontos = niveisGastos(niveis) > 0 ? pontosComNiveis(base, niveis, RULESET_V3) : base
  return {
    ...origem,
    perfil,
    pontos,
    params: paramsDePontos(pontos),
    skin: skinDoTaco(perfil),
    upgrade: origem.origem === 'nft' ? lojaDeUpgrade(perfil, base, niveis, curva, investidoLamports) : null,
  }
}

export const tacoGratuito = (id: CueId): TacoEmJogo => montar({ origem: 'gratuito', id }, TACOS_GRATUITOS[id])

/**
 * O `investidoLamports` é o ÚLTIMO argumento e tem padrão zero de propósito:
 * quem monta um taco só para ler pontos ou params (teste, posse na chain,
 * comparação de equilíbrio) não tem como saber o que foi pago, e não precisa.
 * Quem sabe é o servidor, que lê o livro `taco_upgrade` junto dos níveis.
 */
export const tacoDeNft = (
  mint: string,
  colecao: string,
  perfil: PerfilDeTaco,
  niveis?: Niveis,
  curva?: CurvaDePreco,
  investidoLamports = 0n,
): TacoEmJogo => montar({ origem: 'nft', mint, colecao }, perfil, niveis, curva, investidoLamports)

/**
 * Taco de coleção parceira. Os pontos saem do MODELO da coleção (não do
 * ruleset direto) e não há loja: upgrade pago é da nossa coleção.
 */
export function tacoDeParceira(
  linha: LinhaDaTabela,
  colecao: string,
  modelo: ModeloDePontos,
  urlDaArte: (sha: string) => string,
): TacoEmJogo {
  const perfil = perfilDaLinha(linha)
  // O `por-traits` não olha raridade nem arquétipo: os pontos dele saem dos
  // traits GRAVADOS na linha (e do `especial`, a peça única que nasce full).
  const pontos = pontosDoModelo(modelo, perfil, extraDaLinha(linha))
  return {
    origem: 'parceira',
    mint: linha.mint,
    colecao,
    numero: linha.numero,
    perfil,
    pontos,
    params: paramsDePontos(pontos),
    skin: linha.arte ? urlDaArte(linha.arte) : skinDoTaco(perfil),
    upgrade: null,
  }
}

export const chaveDoTaco = (t: OrigemDoTaco): string =>
  t.origem === 'gratuito' ? `gratuito:${t.id}` : `${t.origem}:${t.mint}`

export const atendePedido = (t: OrigemDoTaco, pedido: PedidoDeTaco): boolean =>
  'mint' in pedido ? t.origem !== 'gratuito' && t.mint === pedido.mint : t.origem === 'gratuito' && t.id === pedido.id

/**
 * O pedido que ESTE taco representaria, na forma que viaja para o servidor.
 *
 * Por `mint` em NFT próprio e em NFT de parceira — os dois só se identificam
 * pelo mint —, e por `id` no gratuito. Um só lugar decide essa forma: a tela
 * e a chamada de `guardarPedido`/`match.commit` usam esta função em vez de
 * repetir o `origem === 'nft'` (que esqueceria a parceira).
 */
export const pedidoDoTaco = (t: OrigemDoTaco): PedidoDeTaco =>
  t.origem === 'gratuito' ? { id: t.id } : { mint: t.mint }
